import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import AddPaymentMethodModal from './AddPaymentMethodModal';
import { 
  CreditCard, 
  Download, 
  Calendar, 
  Shield, 
  Eye, 
  ChevronDown, 
  ChevronUp,
  ArrowUp,
  Pause,
  X,
  Bell,
  FileText,
  Users,
  Video,
  HardDrive,
  Phone,
  Crown,
  Check,
  User,
  Edit3
} from 'lucide-react';

const SubscriptionHub = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  // Price comparison chart is now always visible
  const [showUpgradeDropdown, setShowUpgradeDropdown] = useState(false);
  const [pausingSubscription, setPausingSubscription] = useState(false);
  const [userUsage, setUserUsage] = useState({
    legalMatters: 0,
    publicNotices: 0,
    storage: '0GB',
    legalMattersLimit: 'Unlimited',
    publicNoticesLimit: 'Unlimited',
    storageLimit: 'Unlimited'
  });
  const [usageHistory, setUsageHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Handle upgrade success callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const upgrade = searchParams.get('upgrade');
    
    if (sessionId && upgrade === 'success') {
      // Call fallback endpoint to ensure subscription is updated
      const verifyUpgrade = async () => {
        try {
          console.log('🔄 Verifying upgrade payment...', sessionId);
          const response = await fetch(`${backendUrl}/api/payment/session/${sessionId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Upgrade verified:', data);
            
            // Show success message
            alert('🎉 Subscription upgraded successfully! Your account has been updated.');
            
            // Clear URL parameters
            setSearchParams({});
            
            // Refresh user data
            window.location.reload();
          } else {
            console.error('❌ Failed to verify upgrade');
          }
        } catch (error) {
          console.error('Error verifying upgrade:', error);
        }
      };
      
      verifyUpgrade();
    }
  }, [searchParams, setSearchParams, backendUrl]);

  // Fetch real user usage data and customer info
  useEffect(() => {
    fetchUserUsage();
    fetchCustomerInfo();
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUpgradeDropdown && !event.target.closest('.upgrade-dropdown-container')) {
        setShowUpgradeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUpgradeDropdown]);

  const fetchCustomerInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/billing/customer-info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomerInfo(data);
      }
    } catch (error) {
      console.error('Error fetching customer info:', error);
    }
  };

  const fetchUserUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch user's actual usage from backend
      const response = await fetch(`${backendUrl}/api/user/usage`, {
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }
      });

      if (response.ok) {
        const data = await response.json();
        const currentUsage = data.current_usage || {};
        const planLimits = data.plan_limits || {};
        
        // Calculate storage (approximate based on documents)
        const docCount = currentUsage.documents_generated || 0;
        const storageGB = (docCount * 0.1).toFixed(1);
        
        setUserUsage({
          legalMatters: currentUsage.legal_matters_count || 0,
          publicNotices: currentUsage.templates_downloaded || 0,
          storage: `${storageGB}GB`,
          legalMattersLimit: planLimits.legal_matters || 'Unlimited',
          publicNoticesLimit: planLimits.templates || 'Unlimited',
          storageLimit: planLimits.storage || 'Unlimited'
        });
      } else {
        console.error('Failed to fetch usage data');
      }
      
    } catch (error) {
      console.error('Error fetching usage data:', error);
      // Keep default values on error
    }
  };

  const fetchUsageHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/user/usage/history`, {
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageHistory(data.history || []);
        setShowHistoryModal(true);
      } else {
        console.error('Failed to fetch usage history');
      }
      
    } catch (error) {
      console.error('Error fetching usage history:', error);
    }
  };

  // Plan configurations
  const planConfigs = {
    free: {
      name: 'Free',
      price: 0,
      billing: 'Free',
      limits: {
        legalMatters: 1,
        publicNotices: 1,
        storage: '500MB',
        videoLibrary: 'Partial',
        strategyCalls: '–'
      },
      features: [
        '1 Legal Matter per month',
        '1 Public Notice per month', 
        '500MB Document Vault',
        'Limited Video Library Access'
      ]
    },
    basic: {
      name: 'Justice Seeker',
      price: 9.99,
      billing: '$9.99/month',
      limits: {
        legalMatters: 5,
        publicNotices: 3,
        storage: '2GB',
        videoLibrary: 'Full Access',
        strategyCalls: '–'
      },
      features: [
        '5 Legal Matters per month',
        '3 Public Notices per month',
        '2GB Document Vault',
        'Full Video Library Access'
      ]
    },
    justice_seeker: { // Alias for database compatibility
      name: 'Justice Seeker',
      price: 9.99,
      billing: '$9.99/month',
      limits: {
        legalMatters: 5,
        publicNotices: 3,
        storage: '2GB',
        videoLibrary: 'Full Access',
        strategyCalls: '–'
      },
      features: [
        '5 Legal Matters per month',
        '3 Public Notices per month',
        '2GB Document Vault',
        'Full Video Library Access'
      ]
    },
    premium: {
      name: 'Sovereign Shield',
      price: 97.00,
      billing: '$97/month', 
      limits: {
        legalMatters: 'Unlimited',
        publicNotices: 10,
        storage: '10GB',
        videoLibrary: 'Full Access',
        strategyCalls: 'Monthly'
      },
      features: [
        'Unlimited Legal Templates',
        'Up to 10 Public Notices per month',
        '10GB Document Vault',
        'Monthly Strategy Session'
      ]
    },
    sovereign_shield: { // Alias for database compatibility
      name: 'Sovereign Shield',
      price: 97.00,
      billing: '$97/month', 
      limits: {
        legalMatters: 'Unlimited',
        publicNotices: 10,
        storage: '10GB',
        videoLibrary: 'Full Access',
        strategyCalls: 'Monthly'
      },
      features: [
        'Unlimited Legal Templates',
        'Up to 10 Public Notices per month',
        '10GB Document Vault',
        'Monthly Strategy Session'
      ]
    },
    lifetime: {
      name: 'Defender of the People',
      price: 1497.00,
      billing: '$1497 One-time',
      limits: {
        legalMatters: 'Unlimited',
        publicNotices: 'Unlimited', 
        storage: 'Unlimited',
        videoLibrary: 'Full Access',
        strategyCalls: 'Quarterly'
      },
      features: [
        'Unlimited Legal Templates',
        'Unlimited Public Notices',
        'Unlimited Document Vault',
        'Quarterly Strategy Session'
      ]
    },
    defender_of_the_people: { // Alias for database compatibility
      name: 'Defender of the People',
      price: 1497.00,
      billing: '$1497 One-time',
      limits: {
        legalMatters: 'Unlimited',
        publicNotices: 'Unlimited', 
        storage: 'Unlimited',
        videoLibrary: 'Full Access',
        strategyCalls: 'Quarterly'
      },
      features: [
        'Unlimited Legal Templates',
        'Unlimited Public Notices',
        'Unlimited Document Vault',
        'Quarterly Strategy Session'
      ]
    }
  };

  const currentPlan = planConfigs[user?.subscription_plan] || planConfigs.free;

  const handleUpgradePlan = () => {
    setShowUpgradeDropdown(!showUpgradeDropdown);
  };

  const handleUpgradeToSpecificPlan = async (planId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Please login to upgrade your plan.');
        return;
      }

      // Call the backend subscription upgrade endpoint
      const response = await fetch(`${backendUrl}/api/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planId: planId,
          currentPlan: user?.subscription_plan || 'free'
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // If successful, redirect to Stripe checkout
        if (result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
        } else {
          alert(`Successfully selected ${planId} plan!`);
        }
      } else {
        alert(result.message || 'Error processing plan upgrade. Please try again.');
      }
      
    } catch (error) {
      console.error('Error upgrading plan:', error);
      alert('Error processing plan upgrade. Please try again.');
    } finally {
      setLoading(false);
      setShowUpgradeDropdown(false);
    }
  };

  const handlePauseSubscription = async () => {
    if (!window.confirm('Are you sure you want to pause your subscription? You will lose access to premium features until you resume.')) {
      return;
    }

    setPausingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      
      // Call backend API to pause subscription
      const response = await fetch(`${backendUrl}/api/subscription/pause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        alert('Your subscription has been paused successfully. You can resume it anytime.');
        // Refresh the page or update user state
        window.location.reload();
      } else {
        alert(result.message || 'Failed to pause subscription. Please try again.');
      }
      
    } catch (error) {
      console.error('Error pausing subscription:', error);
      alert('Error pausing subscription. Please try again.');
    } finally {
      setPausingSubscription(false);
    }
  };

  const handleCancelPlan = () => {
    if (window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
      alert('Cancel plan functionality - would process cancellation');
    }
  };

  const handleUpdateBilling = () => {
    // Open the add payment method modal
    setShowAddPaymentModal(true);
  };

  const handlePaymentMethodAdded = (paymentMethod) => {
    // Refresh customer info after adding payment method
    fetchCustomerInfo();
    alert(`Payment method added successfully! ${paymentMethod.card?.brand?.toUpperCase()} ending in ${paymentMethod.card?.last4}`);
  };

  const handleViewBillingHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/billing/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const transactions = await response.json();
        
        // Create a simple modal to show transaction history
        let historyHtml = '<h3>Billing History</h3><ul>';
        transactions.forEach(transaction => {
          historyHtml += `<li>${transaction.date} - ${transaction.description} - $${transaction.amount}</li>`;
        });
        historyHtml += '</ul>';
        
        // For now, show in alert (in production, would use a proper modal)
        if (transactions.length === 0) {
          alert('No billing history found.');
        } else {
          alert(`Found ${transactions.length} transactions. Check console for details.`);
          console.log('Billing History:', transactions);
        }
      } else {
        alert('Error fetching billing history. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching billing history:', error);
      alert('Error fetching billing history. Please try again.');
    }
  };

  const handleDownloadReceipts = () => {
    alert('Download receipts - would generate PDF receipts');
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'free': return 'bg-gray-100 text-gray-700';
      case 'basic': return 'bg-blue-100 text-blue-700';
      case 'premium': return 'bg-purple-100 text-purple-700';
      case 'lifetime': return 'bg-gold-100 text-gold-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getNextBillingDate = () => {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Management Hub</h1>
          <p className="text-gray-600">Manage your plan, billing, and subscription features</p>
        </div>

      {/* Current Plan Summary */}
      <Card className="border-2 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-600" />
              <span>Your Current Plan</span>
              <Badge className={getPlanBadgeColor(user?.subscription_plan)}>
                {currentPlan.name}
              </Badge>
            </div>
            {user?.subscription_plan !== 'free' && (
              <div className="text-right">
                <div className="text-sm text-gray-600">Next Billing Date</div>
                <div className="font-semibold">{getNextBillingDate()}</div>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan Features */}
            <div>
              <h3 className="font-semibold mb-3">Your Plan Includes:</h3>
              <ul className="space-y-2">
                {currentPlan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Plan Actions */}
            <div className="space-y-3">
              <div className="text-right mb-4">
                <div className="text-2xl font-bold text-blue-600">{currentPlan.billing}</div>
              </div>
              
              <div className="flex flex-col gap-2">
                {user?.subscription_plan !== 'free' && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={handlePauseSubscription} 
                      className="w-full"
                      disabled={pausingSubscription}
                    >
                      <Pause className="w-4 h-4 mr-2" />
                      {pausingSubscription ? 'Pausing...' : 'Pause Subscription'}
                    </Button>
                    <Button variant="outline" onClick={handleCancelPlan} className="w-full text-red-600 hover:text-red-800">
                      <X className="w-4 h-4 mr-2" />
                      Cancel Plan
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan Comparison, Usage, and Billing moved to Account Settings page */}

      {/* Sovereign Commitment Statement */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="text-center py-6">
          <p className="text-lg text-gray-700 italic">
            "Your sovereignty is your responsibility. We provide the tools. You make the moves. 
            Manage your access here with clarity, honor, and confidence."
          </p>
        </CardContent>
      </Card>
      
      {/* Add Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        onSuccess={handlePaymentMethodAdded}
      />
      
      {/* Usage History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Full Usage History</h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-150px)]">
              {usageHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No usage history available yet.</p>
                  <p className="text-sm mt-2">Start creating legal matters to see your history here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {usageHistory.map((record, index) => (
                    <div 
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {new Date(record.month + '-01').toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long' 
                          })}
                        </h3>
                        {index === 0 && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            Current Month
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {record.legal_matters_count}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            📋 Legal Matters
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {record.documents_generated}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            📄 Documents
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            {record.templates_downloaded}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            📥 Templates
                          </div>
                        </div>
                      </div>
                      
                      {record.updated_at && (
                        <div className="text-xs text-gray-400 mt-3 text-right">
                          Last updated: {new Date(record.updated_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SubscriptionHub;